// Pure helpers for the workflow builder and the record panels: which collection a flow's trigger is about, the order its
// nodes run in, what changed between two versions of a graph, and the readable name of a field. No React in this file.
import type { EdgeLabel, WorkflowEdge, WorkflowGraph, WorkflowNode } from "@offsilo/contracts/automation";
import type { CollectionName } from "@/data/client";
import type { Catalog } from "./catalog";

/** The event a flow starts on: its trigger node's config.event, or null while the trigger has none. */
export function triggerEventOf(nodes: WorkflowNode[]): string | null {
  return nodes.find((n) => n.kind === "trigger")?.config.event || null;
}

/** The collection the trigger's records belong to, from the catalog. Null when the catalog does not know the event. */
export function collectionOfEvent(catalog: Catalog | undefined, event: string | null): CollectionName | null {
  if (!event) return null;
  return (catalog?.triggers.find((t) => t.event === event)?.collection as CollectionName | undefined) ?? null;
}

/** The label the catalog gives a trigger event, or the event itself when the catalog does not know it. */
export function triggerLabelOf(catalog: Catalog | undefined, event: string): string {
  return catalog?.triggers.find((t) => t.event === event)?.label ?? event;
}

/** "creditNotes" becomes "Credit notes", "dueAt" becomes "Due at". */
export function humanize(name: string): string {
  const s = name.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The nodes in the order a flow runs them: from each start node (one with no incoming line), following the lines depth first. */
export function runOrder(nodes: WorkflowNode[], edges: WorkflowEdge[]): WorkflowNode[] {
  const incoming = new Set(edges.map((e) => e.target));
  const order: WorkflowNode[] = [];
  const seen = new Set<string>();
  const visit = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    const n = nodes.find((x) => x.id === id);
    if (n) order.push(n);
    for (const e of edges.filter((x) => x.source === id)) visit(e.target);
  };
  for (const n of nodes.filter((x) => !incoming.has(x.id) && x.kind !== "note" && x.kind !== "frame")) visit(n.id);
  return order;
}

/** One line of a comparison: a box that was added, changed or removed. */
export type DiffLine = { change: "added" | "changed" | "removed"; label: string };
export type GraphDiff = { lines: DiffLine[]; edgesAdded: number; edgesRemoved: number };

const nodeSignature = (n: WorkflowNode) => JSON.stringify([n.kind, n.label, n.detail ?? "", Object.entries(n.config).sort()]);
const edgeKey = (e: WorkflowEdge) => `${e.source}>${e.target}:${e.label ?? ""}`;

/** What changed going from `from` to `to`, matched by node id. A line counts as added, changed or removed; lines are counted as edges too. */
export function diffGraphs(from: WorkflowGraph, to: WorkflowGraph): GraphDiff {
  const before = new Map(from.nodes.map((n): [string, WorkflowNode] => [n.id, n]));
  const after = new Map(to.nodes.map((n): [string, WorkflowNode] => [n.id, n]));
  const lines: DiffLine[] = [];
  for (const n of to.nodes) {
    const old = before.get(n.id);
    if (!old) lines.push({ change: "added", label: n.label });
    else if (nodeSignature(old) !== nodeSignature(n)) lines.push({ change: "changed", label: n.label });
  }
  for (const n of from.nodes) if (!after.has(n.id)) lines.push({ change: "removed", label: n.label });
  const edgesBefore = new Set(from.edges.map(edgeKey));
  const edgesAfter = new Set(to.edges.map(edgeKey));
  return {
    lines,
    edgesAdded: to.edges.filter((e) => !edgesBefore.has(edgeKey(e))).length,
    edgesRemoved: from.edges.filter((e) => !edgesAfter.has(edgeKey(e))).length,
  };
}

// ---------------------------------------------------------------- lengths of time

/** The units a length of time takes in the builder, as the engine reads them ("30 minutes", "2 days"). */
export type DurationUnit = "minute" | "hour" | "day" | "week";
const UNIT_OF: Record<string, DurationUnit> = { minute: "minute", min: "minute", hour: "hour", hr: "hour", day: "day", week: "week" };

/** "2 days" becomes { amount: 2, unit: "day" }; a length the units cannot say (such as "2 working days") is null. */
export function durationParts(text: string | undefined): { amount: number; unit: DurationUnit } | null {
  const m = (text ?? "").trim().toLowerCase().match(/^(\d+(?:\.\d+)?)\s*(minute|min|hour|hr|day|week)s?$/);
  return m ? { amount: Number(m[1]), unit: UNIT_OF[m[2]!]! } : null;
}

/** An amount and a unit as the engine reads them: "1 day", "2 days", "30 minutes". */
export function durationText(amount: number, unit: DurationUnit): string {
  return `${amount} ${unit}${amount === 1 ? "" : "s"}`;
}

// ---------------------------------------------------------------- rules

/** The comparisons a rule makes: the operator names rule JSON uses (the engine's RULE_OP). */
export const RULE_OPS = ["eq", "ne", "gt", "gte", "lt", "lte", "contains", "in", "empty", "notEmpty", "changed"] as const;
export type RuleOp = (typeof RULE_OPS)[number];
export type RuleRow = { field: string; op: RuleOp; value: string };
/** A rule the simple editor shows: its rows, joined by all or by any. */
export type RuleDraft = { combinator: "all" | "any"; rows: RuleRow[] };

/** Whether an operator compares with a value (the two empty checks do not). */
export const opTakesValue = (op: RuleOp) => op !== "empty" && op !== "notEmpty";

/** Reads rule JSON into the rows the simple editor shows. Null when the rule nests groups or is not a rule: it is then
 *  edited as its JSON text, so nothing is lost. */
export function ruleRows(text: string | undefined): RuleDraft | null {
  if (!text?.trim()) return { combinator: "all", rows: [] };
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return null;
  }
  const obj = typeof json === "object" && json !== null && !Array.isArray(json) ? (json as Record<string, unknown>) : null;
  const combinator = Array.isArray(obj?.all) ? "all" : Array.isArray(obj?.any) ? "any" : null;
  if (!obj || !combinator) return null;
  const rows: RuleRow[] = [];
  for (const item of obj[combinator] as unknown[]) {
    const c = typeof item === "object" && item !== null ? (item as { field?: unknown; op?: unknown; value?: unknown }) : null;
    if (!c || typeof c.field !== "string" || !(RULE_OPS as readonly unknown[]).includes(c.op)) return null;
    const value = Array.isArray(c.value) ? c.value.join(", ") : c.value === undefined || c.value === null ? "" : String(c.value);
    rows.push({ field: c.field, op: c.op as RuleOp, value });
  }
  return { combinator, rows };
}

/** Rule JSON for the rows that name a field; empty while no row does. */
export function ruleText(draft: RuleDraft): string {
  const conditions = draft.rows
    .filter((r) => r.field.trim() !== "")
    .map((r) => (opTakesValue(r.op) && r.value.trim() !== "" ? { field: r.field.trim(), op: r.op, value: r.value.trim() } : { field: r.field.trim(), op: r.op }));
  return conditions.length ? JSON.stringify({ [draft.combinator]: conditions }) : "";
}

// ---------------------------------------------------------------- lines out of a box

/** Which meaning a line has, as the inspector lists it: `case` (a switch's line), `label:<label>` (yes, approved, each ...) or `plain`. */
export function lineKey(e: { label?: string; case?: string }): string {
  if (e.case !== undefined) return "case";
  return e.label ? `label:${e.label}` : "plain";
}

/** The same line with another meaning (see lineKey). Its source and target stay as they are. */
export function withLine(e: WorkflowEdge, key: string, caseText = ""): WorkflowEdge {
  const next: WorkflowEdge = { id: e.id, source: e.source, target: e.target };
  if (key === "case") return { ...next, case: caseText };
  if (key.startsWith("label:")) return { ...next, label: key.slice("label:".length) as EdgeLabel };
  return next;
}
